import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { useImages } from '../context/ImageContext';
import { TripAdvisorOwlIcon } from './TripAdvisorBanner';

interface HeroProps {
  onExploreMenu: () => void;
  onContact?: () => void;
}

export const Hero: React.FC<HeroProps> = ({ onExploreMenu, onContact }) => {
  const { clientImages, slotOverrides } = useImages();
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);

  const heroVideoSrc =
    slotOverrides['site:heroVideo'] ||
    slotOverrides['heroVideo'] ||
    clientImages.heroVideo ||
    '/hero-video.mp4';

  // Automatically start playing video muted on load
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.muted = true;
      setIsMuted(true);
      video
        .play()
        .then(() => setIsPlaying(true))
        .catch(() => setIsPlaying(false));
    }
  }, [heroVideoSrc]);

  const togglePlay = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video
        .play()
        .then(() => setIsPlaying(true))
        .catch(() => {});
    } else {
      video.pause();
      setIsPlaying(false);
    }
  }, []);

  const toggleMute = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    const video = videoRef.current;
    if (!video) return;
    const newMuted = !video.muted;
    video.muted = newMuted;
    setIsMuted(newMuted);
  }, []);

  return (
    <div className="w-full relative overflow-hidden">
      <section
        id="home"
        className="relative text-white pt-2 sm:pt-6 lg:pt-12 pb-6 sm:pb-14 lg:pb-20 overflow-hidden bg-gradient-to-r from-[#0055DA] via-[#0047C4] to-[#0036A3]"
        style={{
          backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 32px, rgba(255, 255, 255, 0.035) 32px, rgba(255, 255, 255, 0.035) 64px), linear-gradient(135deg, #005EE8 0%, #0045BF 55%, #003399 100%)`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6 lg:gap-10 xl:gap-14 items-center">
            
            {/* Left Column: Text content first on mobile and desktop */}
            <div className="lg:col-span-6 xl:col-span-6 text-left order-1">
              
              {/* Ramsgate Main Sands Outline Pill */}
              <div className="mb-2 sm:mb-4">
                <span className="inline-flex items-center px-3 py-0.5 sm:px-3.5 sm:py-1 rounded-full border-2 border-[#FFD000] text-[#FFD000] text-[10.5px] sm:text-[13px] font-heading font-black tracking-wider uppercase select-none">
                  RAMSGATE MAIN SANDS
                </span>
              </div>

              {/* Big Bold Headline: exact 3 lines matching mobile screenshot */}
              <h1
                className="text-left uppercase select-none text-white tracking-[0.01em] leading-[0.9] text-[2.5rem] min-[370px]:text-[2.85rem] min-[420px]:text-[3.25rem] sm:text-6xl lg:text-7xl xl:text-[5rem] flex flex-col gap-0.5 sm:gap-1"
                style={{ fontFamily: "'Anton', 'Impact', sans-serif" }}
              >
                <span>WELCOME TO</span>
                <span>FRANKIE’S AT THE</span>
                <span className="text-[#FFD000] italic">BEACH!</span>
              </h1>

              {/* Paragraph from reference image */}
              <p className="mt-2.5 sm:mt-4 text-[13.5px] sm:text-base md:text-lg text-white/95 leading-relaxed max-w-xl font-normal">
                Come for a bite, stay for the whole day. Enjoy gourmet food, cold drinks, the
                Children&apos;s Funfair and sweet treats — all on Ramsgate Main Sands.
              </p>

              {/* Action Buttons: Yellow EXPLORE THE MENU ↗ + Blue Outlined PLAN YOUR VISIT */}
              <div className="mt-3.5 sm:mt-5 flex flex-wrap items-center gap-2.5 sm:gap-4">
                <button
                  onClick={onExploreMenu}
                  id="btn-explore-menu"
                  className="bg-[#FFD000] hover:bg-[#FFD933] active:scale-95 text-black font-heading font-black text-xs sm:text-sm uppercase tracking-wider px-4.5 sm:px-6 py-2.5 sm:py-3.5 rounded-md shadow-md hover:shadow-lg transition-all flex items-center gap-2 group cursor-pointer"
                  title="Explore the menu"
                  aria-label="Explore the menu"
                >
                  <span>EXPLORE THE MENU</span>
                  <span className="w-5 h-5 rounded bg-black/15 flex items-center justify-center text-xs font-bold leading-none select-none">↗</span>
                </button>

                <button
                  onClick={onContact}
                  id="btn-plan-your-visit"
                  className="bg-[#0047C4]/50 hover:bg-[#0047C4]/80 active:scale-95 text-white font-heading font-black text-xs sm:text-sm uppercase tracking-wider px-4.5 sm:px-6 py-2.5 sm:py-3.5 rounded-md border-2 border-white/90 hover:border-white shadow-md transition-all flex items-center justify-center group cursor-pointer"
                  title="Plan your visit to Ramsgate Main Sands"
                  aria-label="Plan your visit to Ramsgate Main Sands"
                >
                  <span>PLAN YOUR VISIT</span>
                </button>
              </div>

              {/* TripAdvisor Rating Pill - Prominent official logo, bold & compact */}
              <div className="mt-2 sm:mt-2.5">
                <Link
                  to="/tripadvisor"
                  className="inline-flex items-center gap-2 sm:gap-2.5 px-3.5 sm:px-4 py-1.5 sm:py-2 rounded-full bg-white/20 hover:bg-white/30 border border-white/35 text-white transition-all shadow-sm group backdrop-blur-xs"
                  title="View TripAdvisor ratings and reviews"
                  aria-label="TripAdvisor rating 4.9 from 1,000+ reviews"
                >
                  {/* Official TripAdvisor Mascot Icon - prominent and sharp */}
                  <TripAdvisorOwlIcon className="w-5 h-5 sm:w-6 sm:h-6 shrink-0 drop-shadow-xs" />

                  {/* 5 TripAdvisor green circles */}
                  <div className="flex items-center gap-1 sm:gap-1.5" aria-hidden="true">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <span
                        key={i}
                        className="w-3 h-3 sm:w-3.5 sm:h-3.5 rounded-full bg-[#00AA6C] inline-block shadow-2xs group-hover:scale-110 transition-transform"
                      />
                    ))}
                  </div>

                  <span className="font-black text-sm sm:text-base text-white font-sans leading-none tracking-tight">
                    4.9
                  </span>

                  <span className="text-xs sm:text-sm font-semibold text-white/95 leading-none">
                    TripAdvisor · 1,000+ reviews
                  </span>
                </Link>
              </div>

            </div>

            {/* Right Column: Hero Video Feature Card (shows below text on mobile, right on desktop) */}
            <div className="lg:col-span-6 xl:col-span-6 relative mt-2.5 sm:mt-5 lg:mt-0 flex justify-center pb-4 sm:pb-10 order-2 w-full">
              <div className="relative w-full max-w-full sm:max-w-[600px] lg:max-w-[640px] xl:max-w-[680px]">
                {/* Framed Video with clean white border */}
                <div className="relative w-full rounded-2xl sm:rounded-3xl overflow-hidden border-[3px] sm:border-4 border-white shadow-[0_20px_50px_rgba(0,25,70,0.6)] aspect-[16/10] sm:aspect-[4/3] min-h-[220px] sm:min-h-[340px] bg-[#002B7F] group">
                  {/* Hero Video with poster fallback */}
                  <video
                    ref={videoRef}
                    src={heroVideoSrc}
                    poster="/ramsgate-sand.webp"
                    autoPlay
                    muted={isMuted}
                    loop
                    playsInline
                    className="w-full h-full object-cover object-center group-hover:scale-102 transition-transform duration-700"
                    aria-label="Frankie's at the Beach seaside video"
                  />

                  {/* Subtle scrim for contrast */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/10 pointer-events-none" />

                  {/* Top-Right Discreet Play/Pause & Sound Controls */}
                  <div className="absolute top-2.5 right-2.5 sm:top-4 sm:right-4 z-20 flex items-center gap-1.5 sm:gap-2">
                    <button
                      onClick={togglePlay}
                      className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-black/60 hover:bg-black/80 backdrop-blur-md border border-white/40 flex items-center justify-center text-white transition-all active:scale-95 shadow-md cursor-pointer"
                      title={isPlaying ? 'Pause video' : 'Play video'}
                      aria-label={isPlaying ? 'Pause video' : 'Play video'}
                    >
                      {isPlaying ? (
                        <Pause className="w-3 h-3 sm:w-4 sm:h-4 fill-current" />
                      ) : (
                        <Play className="w-3 h-3 sm:w-4 sm:h-4 fill-current ml-0.5" />
                      )}
                    </button>
                    <button
                      onClick={toggleMute}
                      className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-black/60 hover:bg-black/80 backdrop-blur-md border border-white/40 flex items-center justify-center text-white transition-all active:scale-95 shadow-md cursor-pointer"
                      title={isMuted ? 'Unmute sound' : 'Mute sound'}
                      aria-label={isMuted ? 'Unmute sound' : 'Mute sound'}
                    >
                      {isMuted ? (
                        <VolumeX className="w-3 h-3 sm:w-4 sm:h-4" />
                      ) : (
                        <Volume2 className="w-3 h-3 sm:w-4 sm:h-4" />
                      )}
                    </button>
                  </div>

                  {/* Bottom-Right Location Text */}
                  <div className="absolute bottom-2.5 right-2.5 sm:bottom-4 sm:right-4 z-10 pointer-events-none">
                    <span className="font-heading font-black text-[10px] sm:text-xs uppercase tracking-widest text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)] select-none">
                      RAMSGATE · KENT
                    </span>
                  </div>
                </div>

                {/* Floating Card at Bottom-Left hanging down over the bottom edge */}
                <div className="absolute -bottom-3 left-2 sm:-bottom-6 sm:left-5 bg-white rounded-lg sm:rounded-2xl p-2.5 sm:p-4 shadow-xl max-w-[180px] sm:max-w-[240px] z-20 text-left border border-black/5 pointer-events-auto">
                  <h3 className="font-heading font-black text-[11px] sm:text-sm text-[#002B7F] uppercase tracking-tight leading-tight">
                    MAKE A DAY OF IT.
                  </h3>
                  <p className="text-[9.5px] sm:text-xs text-gray-600 mt-0.5 sm:mt-1.5 leading-snug font-medium">
                    Gourmet bites, cold drinks, rides and sweet treats — all by the sea.
                  </p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};
